import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AxiosError } from 'axios';
import { CalendarDays, Pencil, Trash2, Wand2 } from 'lucide-react';
import type { EmployeeLoan, ApiError } from '@pms/shared';
import { payrollService } from '@/services/payroll.service';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

function getErrorMessage(e: unknown): string {
  const ax = e as AxiosError<ApiError>;
  return ax.response?.data?.error?.message ?? 'Something went wrong';
}

function formatCurrency(val: string | number): string {
  const n = typeof val === 'string' ? parseFloat(val) : val;
  return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function formatMonthYear(year: number, month: number): string {
  return new Date(year, month - 1).toLocaleString('en-IN', { month: 'short', year: 'numeric' });
}

/** Months offered in the pickers: from the loan's start month to three years ahead. */
const MONTHS_AHEAD = 36;

const monthKey = (year: number, month: number) => year * 12 + (month - 1);
const keyToValue = (key: number) => `${Math.floor(key / 12)}-${(key % 12) + 1}`;
const valueToYearMonth = (value: string) => {
  const [year, month] = value.split('-').map(Number) as [number, number];
  return { year, month };
};

interface Row {
  key: number;
  year: number;
  month: number;
  entryId: string | null;
  scheduled: number | null;
  deducted: number | null;
  source: 'payment' | 'schedule' | null;
}

interface Props {
  loan: EmployeeLoan;
  onClose: () => void;
}

export function LoanScheduleModal({ loan, onClose }: Props) {
  const qc = useQueryClient();
  const now = new Date();
  const currentKey = monthKey(now.getFullYear(), now.getMonth() + 1);
  const editable = loan.status !== 'closed';

  const [monthValue, setMonthValue] = useState(keyToValue(currentKey));
  const [amount, setAmount] = useState(parseFloat(loan.emi_amount).toFixed(0));
  const [fillFrom, setFillFrom] = useState(keyToValue(currentKey));
  const [fillAmount, setFillAmount] = useState(parseFloat(loan.emi_amount).toFixed(0));

  const scheduleQuery = useQuery({
    queryKey: ['loan-schedule', loan.id],
    queryFn: () => payrollService.getLoanSchedule(loan.id),
  });
  const deductionsQuery = useQuery({
    queryKey: ['loan-deductions', loan.id],
    queryFn: () => payrollService.getLoanDeductions(loan.id),
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['loan-schedule', loan.id] });
    void qc.invalidateQueries({ queryKey: ['loan-deductions', loan.id] });
    void qc.invalidateQueries({ queryKey: ['payroll', 'loans'] });
  };

  const setEntry = useMutation({
    mutationFn: () => {
      const { year, month } = valueToYearMonth(monthValue);
      return payrollService.setLoanScheduleEntry(loan.id, { year, month, deduction_amount: Number(amount) });
    },
    onSuccess: () => { toast.success('Schedule updated'); invalidate(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const deleteEntry = useMutation({
    mutationFn: (entryId: string) => payrollService.deleteLoanScheduleEntry(loan.id, entryId),
    onSuccess: () => { toast.success('Month removed from schedule'); invalidate(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const generate = useMutation({
    mutationFn: () => {
      const { year, month } = valueToYearMonth(fillFrom);
      return payrollService.generateLoanSchedule(loan.id, {
        from_year: year, from_month: month, monthly_amount: Number(fillAmount),
      });
    },
    onSuccess: () => { toast.success('Schedule generated'); invalidate(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const monthOptions = useMemo(() => {
    const [sy, sm] = loan.start_date.split('-').map(Number) as [number, number];
    const first = Math.min(monthKey(sy, sm), currentKey);
    const last = currentKey + MONTHS_AHEAD;
    const opts: { value: string; label: string }[] = [];
    for (let k = first; k <= last; k++) {
      opts.push({ value: keyToValue(k), label: formatMonthYear(Math.floor(k / 12), (k % 12) + 1) });
    }
    return opts;
  }, [loan.start_date, currentKey]);

  const rows = useMemo<Row[]>(() => {
    const byKey = new Map<number, Row>();
    const rowFor = (year: number, month: number) => {
      const key = monthKey(year, month);
      let row = byKey.get(key);
      if (!row) {
        row = { key, year, month, entryId: null, scheduled: null, deducted: null, source: null };
        byKey.set(key, row);
      }
      return row;
    };
    for (const e of scheduleQuery.data ?? []) {
      const row = rowFor(e.year, e.month);
      row.entryId = e.id;
      row.scheduled = parseFloat(e.deduction_amount);
    }
    for (const d of deductionsQuery.data?.deductions ?? []) {
      const row = rowFor(d.year, d.month);
      row.deducted = d.amount;
      row.source = d.source;
    }
    return [...byKey.values()].sort((a, b) => a.key - b.key);
  }, [scheduleQuery.data, deductionsQuery.data]);

  const summary = deductionsQuery.data?.summary;
  const unscheduled = summary ? Math.max(0, summary.remaining - summary.upcoming) : 0;
  const isLoading = scheduleQuery.isLoading || deductionsQuery.isLoading;
  const isError = scheduleQuery.isError || deductionsQuery.isError;
  const employeeName = [loan.first_name, loan.last_name].filter(Boolean).join(' ') || '—';

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <CalendarDays className="h-4 w-4 text-indigo-600" />
            Deduction Schedule
          </DialogTitle>
        </DialogHeader>

        {/* Summary */}
        <div className="rounded-lg bg-muted/50 px-4 py-3 space-y-2">
          <p className="text-sm font-medium">Employee: <span className="font-semibold">{employeeName}</span></p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <div>
              <p className="text-xs text-muted-foreground">Total Loan</p>
              <p className="font-semibold">{formatCurrency(loan.amount)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Recovered</p>
              <p className="font-semibold text-emerald-600">{summary ? formatCurrency(summary.recovered) : '—'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Remaining</p>
              <p className="font-semibold text-rose-600">{summary ? formatCurrency(summary.remaining) : '—'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Scheduled Ahead</p>
              <p className="font-semibold text-indigo-600">{summary ? formatCurrency(summary.upcoming) : '—'}</p>
            </div>
          </div>
          {unscheduled > 0 && editable && (
            <p className="text-xs text-amber-700">
              {formatCurrency(unscheduled)} of the remaining amount is not scheduled in any month yet.
            </p>
          )}
        </div>

        <p className="text-xs text-muted-foreground">
          Each month's amount is deducted from that month's salary automatically. A month with no
          entry deducts nothing. Remove a month to skip it.
        </p>

        {editable && (
          <div className="space-y-3">
            {/* Single month */}
            <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
              <div className="flex-1 space-y-1">
                <Label className="text-xs">Month</Label>
                <Select value={monthValue} onValueChange={setMonthValue}>
                  <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {monthOptions.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:w-36 space-y-1">
                <Label className="text-xs">Amount (₹)</Label>
                <Input
                  type="number"
                  min={1}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="h-9"
                />
              </div>
              <Button
                size="sm"
                className="h-9 shrink-0"
                disabled={Number(amount) <= 0 || setEntry.isPending}
                onClick={() => setEntry.mutate()}
              >
                Set Month
              </Button>
            </div>

            {/* Auto-fill */}
            <div className="rounded-md border border-dashed border-indigo-200 bg-indigo-50/40 p-3 space-y-2">
              <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
                <div className="flex-1 space-y-1">
                  <Label className="text-xs">Auto-fill from</Label>
                  <Select value={fillFrom} onValueChange={setFillFrom}>
                    <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {monthOptions.map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:w-36 space-y-1">
                  <Label className="text-xs">Per month (₹)</Label>
                  <Input
                    type="number"
                    min={1}
                    value={fillAmount}
                    onChange={(e) => setFillAmount(e.target.value)}
                    className="h-9"
                  />
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-9 shrink-0"
                  disabled={Number(fillAmount) <= 0 || generate.isPending}
                  onClick={() => generate.mutate()}
                >
                  <Wand2 className="h-4 w-4" /> Auto-fill
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Replaces every month from the chosen one onward, splitting what is still owed into
                equal monthly deductions.
              </p>
            </div>
          </div>
        )}

        {/* Schedule table */}
        <div className="rounded-md border overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-primary text-primary-foreground">
                <th className="px-4 py-2 text-left text-xs font-semibold">Month</th>
                <th className="px-4 py-2 text-right text-xs font-semibold">Scheduled</th>
                <th className="px-4 py-2 text-right text-xs font-semibold">Deducted</th>
                <th className="px-4 py-2 text-left text-xs font-semibold">Status</th>
                {editable && <th className="px-4 py-2 text-right text-xs font-semibold" />}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-center text-sm text-muted-foreground">Loading…</td>
                </tr>
              ) : isError ? (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-center text-sm">
                    <span className="text-destructive">Failed to load schedule.</span>{' '}
                    <button
                      className="text-teal-700 underline"
                      onClick={() => { void scheduleQuery.refetch(); void deductionsQuery.refetch(); }}
                    >
                      Retry
                    </button>
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-center text-sm text-muted-foreground">
                    No months scheduled — nothing will be deducted from salary. Use Auto-fill to set one up.
                  </td>
                </tr>
              ) : (
                rows.map((row) => {
                  const status =
                    row.source === 'payment' ? { label: 'Paid manually', cls: 'bg-blue-100 text-blue-700' }
                    : row.deducted === null ? { label: 'Not deducted', cls: 'bg-slate-100 text-slate-500' }
                    : row.key < currentKey ? { label: 'Deducted', cls: 'bg-emerald-100 text-emerald-700' }
                    : row.key === currentKey ? { label: 'This month', cls: 'bg-teal-100 text-teal-700' }
                    : { label: 'Upcoming', cls: 'bg-indigo-100 text-indigo-700' };
                  return (
                    <tr key={row.key} className="border-t border-border/50">
                      <td className="px-4 py-2.5 font-medium">{formatMonthYear(row.year, row.month)}</td>
                      <td className="px-4 py-2.5 text-right">
                        {row.scheduled !== null ? formatCurrency(row.scheduled) : <span className="text-muted-foreground">—</span>}
                      </td>
                      <td className="px-4 py-2.5 text-right font-medium text-emerald-600">
                        {row.deducted !== null ? formatCurrency(row.deducted) : <span className="text-muted-foreground">—</span>}
                      </td>
                      <td className="px-4 py-2.5">
                        <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${status.cls}`}>
                          {status.label}
                        </span>
                      </td>
                      {editable && (
                        <td className="px-4 py-2.5">
                          {row.entryId && (
                            <div className="flex justify-end gap-1">
                              <button
                                title="Edit"
                                className="rounded p-1 text-muted-foreground hover:bg-muted"
                                onClick={() => {
                                  setMonthValue(keyToValue(row.key));
                                  setAmount(String(row.scheduled ?? ''));
                                }}
                              >
                                <Pencil className="h-4 w-4" />
                              </button>
                              <button
                                title="Remove month"
                                className="rounded p-1 text-destructive hover:bg-destructive/10"
                                disabled={deleteEntry.isPending}
                                onClick={() => deleteEntry.mutate(row.entryId!)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="flex justify-end">
          <Button variant="outline" onClick={onClose}>Done</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
